import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, ArrowLeft, Trash2 } from "lucide-react";
import { useJotFormForms, useJotFormForm } from "@/hooks/useJotFormForms";
import { useProject } from "@/hooks/useProject";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { QuestionGroupSection } from "./components/QuestionGroupSection";

export default function JotFormFormConfig() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const jotformId = searchParams.get("jotform_id");

  const { projectId } = useProject();
  const {
    configuredForms,
    jotformForms,
    saveForm,
    isSaving,
    deleteForm,
    isDeleting,
  } = useJotFormForms();

  const formId = id === "new" ? null : parseInt(id || "0");
  const { data: existingForm, isLoading: isLoadingForm } = useJotFormForm(formId);

  const [formTitle, setFormTitle] = useState("");
  const [isReferralForm, setIsReferralForm] = useState(false);
  const [parentFormId, setParentFormId] = useState<number | null>(null);
  const [validationThreshold, setValidationThreshold] = useState(100);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Load projects
  useEffect(() => {
    async function loadProjects() {
      try {
        const { data, error } = await supabase
          .from("projects")
          .select("*")
          .eq("status", "active")
          .order("name");

        if (error) throw error;

        setProjects(data || []);
        if (projectId) {
          setSelectedProjectId(projectId);
        }
      } catch (error) {
        console.error("Error loading projects:", error);
        toast.error("Error al cargar proyectos");
      } finally {
        setLoadingProjects(false);
      }
    }

    loadProjects();
  }, [projectId]);

  const [isFormInitialized, setIsFormInitialized] = useState(false);

  // Populate form when editing (only once)
  useEffect(() => {
    if (isFormInitialized) return;

    if (existingForm && !isLoadingForm) {
      setFormTitle(existingForm.form_title || "");
      setIsReferralForm(existingForm.is_referral_form);
      setParentFormId(existingForm.parent_form_id);
      setValidationThreshold(existingForm.validation_threshold);
      setSelectedProjectId(existingForm.project_id);
      setIsFormInitialized(true);
    } else if (jotformId && jotformForms.length > 0) {
      // New form - try to get title from JotForm API
      const jotformForm = jotformForms.find((f) => f.id === jotformId);
      if (jotformForm) {
        setFormTitle(jotformForm.title);
        setIsFormInitialized(true);
      }
    }
  }, [existingForm, jotformId, jotformForms, isFormInitialized, isLoadingForm]);

  // Filter parent forms (only non-referral forms)
  const availableParentForms = configuredForms.filter(
    (f) => !f.is_referral_form && f.id !== formId
  );

  const handleSave = async () => {
    if (!formTitle.trim()) {
      toast.error("El título es requerido");
      return;
    }

    if (isReferralForm && !parentFormId) {
      toast.error("Los formularios de referidos deben tener un formulario padre");
      return;
    }

    if (!selectedProjectId) {
      toast.error("Debes seleccionar un proyecto");
      return;
    }

    try {
      const formData = {
        id: formId || undefined,
        jotform_form_id: existingForm?.jotform_form_id || jotformId || "",
        form_title: formTitle,
        is_referral_form: isReferralForm,
        parent_form_id: isReferralForm ? parentFormId : null,
        validation_threshold: validationThreshold,
      };

      // Override project_id manually
      const dataToSave: any = {
        project_id: selectedProjectId,
        jotform_form_id: formData.jotform_form_id,
        form_title: formData.form_title,
        is_referral_form: formData.is_referral_form,
        parent_form_id: formData.parent_form_id,
        validation_threshold: formData.validation_threshold,
      };

      let result;
      if (formData.id) {
        // Update
        const { data, error } = await supabase
          .from("jotform_form_mappings")
          .update(dataToSave)
          .eq("id", formData.id)
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        // Insert
        const { data, error } = await supabase
          .from("jotform_form_mappings")
          .insert(dataToSave)
          .select()
          .single();

        if (error) throw error;
        result = data;
      }

      toast.success("Formulario guardado exitosamente");
      navigate("/admin/jotform-forms");
    } catch (error: any) {
      console.error("Error saving form:", error);
      toast.error(error.message || "Error al guardar el formulario");
    }
  };

  const handleDelete = async () => {
    if (!formId) return;

    try {
      await deleteForm(formId);
      toast.success("Formulario eliminado exitosamente");
      navigate("/admin/jotform-forms");
    } catch (error: any) {
      console.error("Error deleting form:", error);
      toast.error(error.message || "Error al eliminar el formulario");
    }
  };

  if (isLoadingForm || loadingProjects) {
    return (
      <div className="mx-auto p-6 space-y-6 max-w-6xl">
        <Skeleton className="h-4 w-28" />
        <div className="flex justify-between items-center">
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <Skeleton className="h-10 w-full" />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-56" />
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-72" />
              </div>
              <Skeleton className="h-6 w-11" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto p-6 space-y-6 max-w-6xl">
      <div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate("/admin/forms")}
          className="inline-flex items-center text-sm text-primary hover:underline px-0"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Volver a la lista
        </Button>
      </div>
      
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">
            {formId ? "Editar" : "Nuevo"} Formulario
          </h1>
          <p className="text-muted-foreground mt-1">
            Configura los parámetros del formulario
          </p>
        </div>
        {formId && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowDeleteDialog(true)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>

      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="basic">Información Básica</TabsTrigger>
          <TabsTrigger value="groups" disabled={!formId}>
            Agrupación de Preguntas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Información Básica</CardTitle>
              <CardDescription>
                Datos generales del formulario
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Título del Formulario</Label>
                <Input
                  id="title"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="Ej: Formulario de Evaluación 3M"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="project">Proyecto</Label>
                <Select
                  value={selectedProjectId?.toString() || ""}
                  onValueChange={(value) => setSelectedProjectId(parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar proyecto" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id.toString()}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {existingForm && (
                <div className="space-y-2">
                  <Label>ID de JotForm</Label>
                  <div className="text-sm text-muted-foreground font-mono">
                    {existingForm.jotform_form_id}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Configuración de Referidos</CardTitle>
              <CardDescription>
                Configura si este formulario es para referidos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="is-referral">¿Es un formulario de referidos?</Label>
                  <div className="text-sm text-muted-foreground">
                    Los formularios de referidos deben tener un formulario padre
                  </div>
                </div>
                <Switch
                  id="is-referral"
                  checked={isReferralForm}
                  onCheckedChange={(checked) => {
                    console.log("Switch changed:", checked);
                    setIsReferralForm(checked);
                    if (!checked) {
                      setParentFormId(null);
                    }
                  }}
                />
              </div>

              {isReferralForm && (
                <div className="space-y-2">
                  <Label htmlFor="parent">Formulario Padre</Label>
                  <Select
                    value={parentFormId?.toString() || ""}
                    onValueChange={(value) => setParentFormId(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar formulario padre" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableParentForms.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">
                          No hay formularios principales disponibles
                        </div>
                      ) : (
                        availableParentForms.map((form) => (
                          <SelectItem key={form.id} value={form.id.toString()}>
                            {form.form_title || form.jotform_form_id}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Umbral de Validación</CardTitle>
              <CardDescription>
                Porcentaje mínimo de validez para las respuestas (0-100)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <Label>Umbral: {validationThreshold}%</Label>
                </div>
                <Slider
                  value={[validationThreshold]}
                  onValueChange={(value) => setValidationThreshold(value[0])}
                  min={0}
                  max={100}
                  step={5}
                  className="w-full"
                />
                <div className="text-xs text-muted-foreground">
                  Las respuestas con un puntaje de validez menor a este umbral serán marcadas como inválidas
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="groups" className="space-y-6">
          {!formId ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                Guarda el formulario primero para configurar grupos de preguntas
              </CardContent>
            </Card>
          ) : (
            <QuestionGroupSection formId={existingForm?.jotform_form_id || ""} />
          )}
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => navigate("/admin/jotform-forms")}
          disabled={isSaving}
        >
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Guardar
            </>
          )}
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la configuración del formulario. Los datos de las
              respuestas no se verán afectados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
